import React from 'react';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { Button } from '../../common/ui/Button';
import {
  getSectors,
  getSubsectorsBySector,
  getPlatforms,
  getTools,
  getProgrammingLanguages,
  type TaxonomyTerm,
  type Sector,
} from '../../../lib/taxonomyService';
import { WorkspaceImageUpload } from '../WorkspaceImageUpload';
import { TagInput } from '../../common/input/TagInput';
import {
  Workspace,
  WorkspaceTaxonomies,
  Tag,
  ValidationErrors,
  DynamicTagInputProps,
} from '../types';
import {
  organizationSizes,
  organizationTypes,
  WORKSPACE_UPDATED_EVENT,
} from '../constants';
import BaseCard from '../../common/layout/BaseCard';
import { captureAppException, createSupabaseError } from '../../../utils/errorCaptureUtils';

const DynamicTagInput: React.FC<DynamicTagInputProps> = ({
  selectedTags,
  onTagsChange,
  tableName,
  tagType,
}) => {
  const searchTags = async (searchTerm: string) => {
    if (!searchTerm) return [];
    const { data } = await supabase
      .from(tableName)
      .select('id, name')
      .ilike('name', `%${searchTerm}%`)
      .limit(10);

    return (data || []).map((item: { id: number; name: string }) => ({
      id: item.id.toString(),
      name: item.name,
      type: tagType,
    }));
  };

  return (
    <TagInput
      selectedTags={selectedTags}
      onTagsChange={onTagsChange}
      searchTags={searchTags}
    />
  );
};

export const ManageWorkspaceGeneral = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [workspaceTaxonomies, setWorkspaceTaxonomies] =
    useState<WorkspaceTaxonomies>({
      countries: [],
      industries: [],
      platforms: [],
      programming_languages: [],
      tools: [],
      sectors: [],
      subsectors: [],
    });

  const [sectors, setSectors] = useState<Sector[]>([]);
  const [allSubsectors, setAllSubsectors] = useState<TaxonomyTerm[]>([]);
  const [platforms, setPlatforms] = useState<TaxonomyTerm[]>([]);
  const [tools, setTools] = useState<TaxonomyTerm[]>([]);
  const [programmingLanguages, setProgrammingLanguages] = useState<
    TaxonomyTerm[]
  >([]);

  const [countryTags, setCountryTags] = useState<Tag[]>([]);
  const [industryTags, setIndustryTags] = useState<Tag[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  const validateWorkspaceData = (ws: Workspace): ValidationErrors => {
    const errs: ValidationErrors = {};
    if (!ws.workspace_name?.trim())
      errs.workspace_name = 'Workspace name is required';
    if (ws.founded_year !== null && ws.founded_year !== undefined) {
      if (ws.founded_year < 1800 || ws.founded_year > new Date().getFullYear()) {
        errs.founded_year = `Year must be between 1800 and ${new Date().getFullYear()}`;
      }
    }
    if (ws.website_url) {
      try {
        new URL(ws.website_url);
      } catch {
        errs.website_url = 'Please enter a valid website URL';
      }
    }
    if (ws.linkedin_profile_url) {
      try {
        new URL(ws.linkedin_profile_url);
      } catch {
        errs.linkedin_profile_url = 'Please enter a valid LinkedIn URL';
      }
    }
    return errs;
  };

  const toggleTaxonomyItem = (
    key: keyof WorkspaceTaxonomies,
    itemId: string
  ) => {
    const currentValues = workspaceTaxonomies[key];
    const newValues = currentValues.includes(itemId)
      ? currentValues.filter((v) => v !== itemId)
      : [...currentValues, itemId];
    setWorkspaceTaxonomies({ ...workspaceTaxonomies, [key]: newValues });
  };

  const handleSectorChange = (sectorId: string) => {
    const updatedSectors = workspaceTaxonomies.sectors.includes(sectorId)
      ? workspaceTaxonomies.sectors.filter((s) => s !== sectorId)
      : [...workspaceTaxonomies.sectors, sectorId];

    const validSubsectors = workspaceTaxonomies.subsectors.filter((sub) =>
      allSubsectors.some(
        (s) =>
          updatedSectors.includes(s.sector_id?.toString() || '') &&
          s.id === sub
      )
    );

    setWorkspaceTaxonomies({
      ...workspaceTaxonomies,
      sectors: updatedSectors,
      subsectors: validSubsectors,
    });
  };

  useEffect(() => {
    const loadTaxonomies = async () => {
      try {
        const [secData, platData, toolData, langData] = await Promise.all([
          getSectors(),
          getPlatforms(),
          getTools(),
          getProgrammingLanguages(),
        ]);
        setSectors(secData);
        setPlatforms(platData);
        setTools(toolData);
        setProgrammingLanguages(langData);

        const subPromises = secData.map((sector) =>
          getSubsectorsBySector(sector.id)
        );
        const allSubs = await Promise.all(subPromises);
        setAllSubsectors(allSubs.flat());
      } catch (err) {
        console.error('Error loading taxonomies:', err);
        setError('Failed to load form options');
      }
    };
    loadTaxonomies();
  }, []);

  useEffect(() => {
    const fetchWorkspace = async () => {
      try {
        const { data: ws, error: wErr } = await supabase
          .from('workspaces')
          .select(
            `
              *,
              workspace_sectors(sector_id),
              workspace_subsectors(subsector_id),
              workspace_platforms(platform_id),
              workspace_tools(tool_id),
              workspace_programming_languages(programming_language_id),
              workspace_countries(country:countries(id, name)),
              workspace_industries(industry:industries(id, name))
            `
          )
          .eq('workspace_id', id)
          .single();
        if (wErr) throw wErr;

        setWorkspaceTaxonomies({
          sectors: ws.workspace_sectors?.map((s: any) => s.sector_id) || [],
          subsectors:
            ws.workspace_subsectors?.map((s: any) => s.subsector_id) || [],
          platforms: ws.workspace_platforms?.map((p: any) => p.platform_id) || [],
          programming_languages:
            ws.workspace_programming_languages?.map(
              (p: any) => p.programming_language_id
            ) || [],
          tools: ws.workspace_tools?.map((t: any) => t.tool_id) || [],
          countries: ws.workspace_countries?.map((c: any) => c.country.id) || [],
          industries:
            ws.workspace_industries?.map((i: any) => i.industry.id) || [],
        });

        setCountryTags(
          ws.workspace_countries?.map((c: any) => ({
            id: c.country.id,
            name: c.country.name,
            type: 'country',
          })) || []
        );
        setIndustryTags(
          ws.workspace_industries?.map((i: any) => ({
            id: i.industry.id,
            name: i.industry.name,
            type: 'industry',
          })) || []
        );
        setWorkspace(ws);
      } catch (err) {
        console.error('Error fetching workspace:', err);
        setError('Failed to fetch workspace');
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchWorkspace();
  }, [id]);

  const handleLogoUpload = async (file: File) => {
    if (!workspace) return;
    const ts = Date.now();
    const rand = Math.random().toString(36).substring(2, 15);
    const path = `${workspace.workspace_id}/logo-${ts}-${rand}.png`;

    try {
      if (workspace.logo_url) {
        const { data: existing } = await supabase.storage
          .from('workspace-logos')
          .list(workspace.workspace_id);
        if (existing && existing.length > 0) {
          const filesToDelete = existing.map(
            (f) => `${workspace.workspace_id}/${f.name}`
          );
          await supabase.storage.from('workspace-logos').remove(filesToDelete);
        }
      }
      const { error: uploadErr } = await supabase.storage
        .from('workspace-logos')
        .upload(path, file, { cacheControl: '0', upsert: true });
      if (uploadErr) throw uploadErr;

      const {
        data: { publicUrl },
      } = supabase.storage.from('workspace-logos').getPublicUrl(path);

      const { error: updateErr } = await supabase
        .from('workspaces')
        .update({ logo_url: publicUrl })
        .eq('workspace_id', workspace.workspace_id);
      if (updateErr) throw updateErr;

      setWorkspace((prev) =>
        prev ? { ...prev, logo_url: `${publicUrl}?t=${Date.now()}` } : null
      );
    } catch (err) {
      console.error('Logo upload failed:', err);
      setError('Failed to upload workspace logo');
    }
  };

  const handleSave = async () => {
    if (!workspace) return;
    const errors = validateWorkspaceData(workspace);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSaving(true);
    setError(null);

    try {
      const toUpdate = {
        workspace_name: workspace.workspace_name,
        description: workspace.description,
        organization_size: workspace.organization_size,
        organization_type: workspace.organization_type,
        website_url: workspace.website_url,
        linkedin_profile_url: workspace.linkedin_profile_url,
        x_handle: workspace.x_handle,
        founded_year: workspace.founded_year,
        is_public: workspace.is_public,
        logo_url: workspace.logo_url,
        allow_auto_join: false,
        email_domains: [],
      };

      const { error: updateError } = await supabase
        .from('workspaces')
        .update(toUpdate)
        .eq('workspace_id', workspace.workspace_id);

      if (updateError) {
        throw createSupabaseError(updateError, 'update_workspace');
      }

      window.dispatchEvent(
        new CustomEvent(WORKSPACE_UPDATED_EVENT, {
          detail: { workspaceId: workspace.workspace_id },
        })
      );

      const tableMappings = [
        { table: 'workspace_countries', key: 'countries', column: 'country_id' },
        {
          table: 'workspace_industries',
          key: 'industries',
          column: 'industry_id',
        },
        {
          table: 'workspace_platforms',
          key: 'platforms',
          column: 'platform_id',
        },
        { table: 'workspace_tools', key: 'tools', column: 'tool_id' },
        {
          table: 'workspace_programming_languages',
          key: 'programming_languages',
          column: 'programming_language_id',
        },
        { table: 'workspace_sectors', key: 'sectors', column: 'sector_id' },
        {
          table: 'workspace_subsectors',
          key: 'subsectors',
          column: 'subsector_id',
        },
      ];

      await Promise.all(
        tableMappings.map(async (m) => {
          await supabase
            .from(m.table)
            .delete()
            .eq('workspace_id', workspace.workspace_id);

          if (workspaceTaxonomies[m.key].length > 0) {
            await supabase.from(m.table).insert(
              workspaceTaxonomies[m.key].map((val: string) => ({
                workspace_id: workspace.workspace_id,
                [m.column]: val,
              }))
            );
          }
        })
      );

      navigate(`/workspace/${workspace.workspace_id}`);
    } catch (err) {
      captureAppException(err, {
        tags: {
          context: 'manage_workspace_general',
          workspace_id: workspace.workspace_id,
          action: 'save_workspace'
        }
      });
      setError(err instanceof Error ? err.message : 'Failed to save workspace');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!workspace) {
    return <div>Workspace not found.</div>;
  }

  return (
    <BaseCard>
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 py-4 border-b border-gray-200 px-6">
        <h2 className="text-xl font-bold text-gray-900">General Settings</h2>
        <div className="flex gap-4 self-start">
          <Button
            variant="outline"
            size="small"
            onClick={() => navigate(`/workspace/${workspace.workspace_id}`)}
          >
            Cancel
          </Button>
          <Button
            variant="filled"
            size="small"
            onClick={handleSave}
            disabled={saving}
          >
            Save Changes
          </Button>
        </div>
      </div>
      
      {error && (
        <div className="m-6 p-4 text-sm text-red-700 bg-red-100 rounded-lg">{error}</div>
      )}

      <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <WorkspaceImageUpload
            currentImageUrl={workspace.logo_url ?? undefined}
            onImageUpload={handleLogoUpload}
          />

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Workspace Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={workspace.workspace_name}
              onChange={(e) =>
                setWorkspace({ ...workspace, workspace_name: e.target.value })
              }
              className={`mt-1 block w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 ${
                validationErrors.workspace_name
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
            />
            {validationErrors.workspace_name && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.workspace_name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              value={workspace.description || ''}
              onChange={(e) =>
                setWorkspace({ ...workspace, description: e.target.value })
              }
              rows={3}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Organization Type
            </label>
            <select
              value={workspace.organization_type || ''}
              onChange={(e) =>
                setWorkspace({ ...workspace, organization_type: e.target.value })
              }
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select organization type</option>
              {organizationTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Organization Size
            </label>
            <select
              value={workspace.organization_size || ''}
              onChange={(e) =>
                setWorkspace({
                  ...workspace,
                  organization_size: e.target.value,
                })
              }
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select organization size</option>
              {organizationSizes.map((size) => (
                <option key={size.value} value={size.value}>
                  {size.label} employees
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Founded Year
            </label>
            <input
              type="number"
              value={workspace.founded_year || ''}
              onChange={(e) =>
                setWorkspace({
                  ...workspace,
                  founded_year:
                    e.target.value === '' ? null : +e.target.value,
                })
              }
              min="1800"
              max={new Date().getFullYear()}
              className={`mt-1 block w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 ${
                validationErrors.founded_year
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="Enter founding year"
            />
            {validationErrors.founded_year && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.founded_year}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Website URL
            </label>
            <input
              type="url"
              value={workspace.website_url || ''}
              onChange={(e) =>
                setWorkspace({ ...workspace, website_url: e.target.value })
              }
              className={`mt-1 block w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 ${
                validationErrors.website_url
                  ? 'border-red-500'
                  : 'border-gray-300'
              }`}
              placeholder="https://example.com"
            />
            {validationErrors.website_url && (
              <p className="mt-1 text-sm text-red-600">
                {validationErrors.website_url}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              LinkedIn Profile URL
            </label>
            <input
              type="url"
              value={workspace.linkedin_profile_url || ''}
              onChange={(e) =>
                setWorkspace({
                  ...workspace,
                  linkedin_profile_url: e.target.value,
                })
              }
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder="https://linkedin.com/company/example"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              X (Twitter) Handle
            </label>
            <div className="mt-1 flex rounded-lg shadow-xs">
              <span className="inline-flex items-center px-3 rounded-l-lg border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                @
              </span>
              <input
                type="text"
                value={workspace.x_handle || ''}
                onChange={(e) =>
                  setWorkspace({ ...workspace, x_handle: e.target.value })
                }
                className="flex-1 block w-full px-3 py-2 rounded-none rounded-r-lg border border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                placeholder="example"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Visibility
              </label>
              <Button
                variant={workspace.is_public ? 'outline' : 'filled'}
                size="small"
                onClick={() =>
                  setWorkspace({ ...workspace, is_public: !workspace.is_public })
                }
              >
                {workspace.is_public ? 'Public' : 'Private'}
              </Button>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {workspace.is_public
                ? 'Your workspace is visible to everyone'
                : 'Your workspace is only visible to members'}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Your Interests
            </h3>
            <div className="flex flex-wrap gap-2">
              {sectors.map((sector) => (
                <Button
                  key={sector.id}
                  onClick={() => handleSectorChange(sector.id.toString())}
                  variant={
                    workspaceTaxonomies.sectors.includes(sector.id.toString())
                      ? 'filled'
                      : 'outline'
                  }
                  size="small"
                  className="rounded-full"
                >
                  {sector.name}
                </Button>
              ))}
            </div>
          </div>

          {sectors.map((sector) => {
            const isActive = workspaceTaxonomies.sectors.includes(
              sector.id.toString()
            );
            if (!isActive) return null;
            return (
              <div key={sector.id} className="mb-6">
                <h4 className="text-base font-medium text-gray-700 mb-2">
                  {sector.name}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {allSubsectors
                    .filter((sub) => sub.sector_id === sector.id)
                    .map((sub) => (
                      <Button
                        key={sub.id}
                        onClick={() =>
                          toggleTaxonomyItem('subsectors', sub.id.toString())
                        }
                        variant={
                          workspaceTaxonomies.subsectors.includes(
                            sub.id.toString()
                          )
                            ? 'filled'
                            : 'outline'
                        }
                        size="small"
                        className="rounded-full"
                      >
                        {sub.name}
                      </Button>
                    ))}
                </div>
              </div>
            );
          })}

          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Experience
            </h3>
            <div className="mb-6">
              <h4 className="text-base font-medium text-gray-700 mb-2">
                Platforms
              </h4>
              <div className="flex flex-wrap gap-2">
                {platforms.map((p) => (
                  <Button
                    key={p.id}
                    onClick={() =>
                      toggleTaxonomyItem('platforms', p.id.toString())
                    }
                    variant={
                      workspaceTaxonomies.platforms.includes(p.id.toString())
                        ? 'filled'
                        : 'outline'
                    }
                    size="small"
                    className="rounded-full"
                  >
                    {p.name}
                  </Button>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-base font-medium text-gray-700 mb-2">
                Tools
              </h4>
              <div className="flex flex-wrap gap-2">
                {tools.map((tool) => (
                  <Button
                    key={tool.id}
                    onClick={() => toggleTaxonomyItem('tools', tool.id.toString())}
                    variant={
                      workspaceTaxonomies.tools.includes(tool.id.toString())
                        ? 'filled'
                        : 'outline'
                    }
                    size="small"
                    className="rounded-full"
                  >
                    {tool.name}
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <h4 className="text-base font-medium text-gray-700 mb-2">
                Programming Languages
              </h4>
              <div className="flex flex-wrap gap-2">
                {programmingLanguages.map((lang) => (
                  <Button
                    key={lang.id}
                    onClick={() =>
                      toggleTaxonomyItem(
                        'programming_languages',
                        lang.id.toString()
                      )
                    }
                    variant={
                      workspaceTaxonomies.programming_languages.includes(
                        lang.id.toString()
                      )
                        ? 'filled'
                        : 'outline'
                    }
                    size="small"
                    className="rounded-full"
                  >
                    {lang.name}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Countries
            </label>
            <DynamicTagInput
              selectedTags={countryTags}
              onTagsChange={(tags) => {
                setCountryTags(tags);
                setWorkspaceTaxonomies({
                  ...workspaceTaxonomies,
                  countries: tags.map((t) => t.id),
                });
              }}
              tableName="countries"
              tagType="country"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Industries
            </label>
            <DynamicTagInput
              selectedTags={industryTags}
              onTagsChange={(tags) => {
                setIndustryTags(tags);
                setWorkspaceTaxonomies({
                  ...workspaceTaxonomies,
                  industries: tags.map((t) => t.id),
                });
              }}
              tableName="industries"
              tagType="industry"
            />
          </div>
        </div>
      </div>
    </BaseCard>
  );
}; 